import type { Theme, Breakpoint } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid2';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { _socials } from 'src/_mock';
import AppStore from 'src/assets/images/app-store.png';
import FooterWave from 'src/assets/images/footer-wave.png';
import GooglePlay from 'src/assets/images/google-play.png';

import { Logo } from 'src/components/logo';
import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

const LINKS = [
  {
    headline: 'Hızlı Linkler',
    children: [
      { name: 'Anasayfa', href: '/' },
      {
        name: 'Aktiviteler',
        href: paths.aktivite.root,
      },
      {
        name: 'Hakkımızda',
        href: paths.about,
      },
      {
        name: 'İletişim',
        href: paths.contact,
      },
      { name: 'Koşullar', href: paths.conditions },
      // { name: 'Page', href: paths.sss },
    ],
  },
];

const KESFET = [
  { name: 'Nasıl Çalışır?', href: paths.howItWorks || '/nasil-calisir' },
  { name: 'Misyonumuz', href: paths.ourMission || '/misyonumuz' },
  { name: 'Organizatör Kaydı', href: paths.forOrganizer || '/organizator-kaydi' },
  { name: 'Sıkça Sorulan Sorular', href: paths.sss },
];

// ----------------------------------------------------------------------

const FooterRoot = styled('footer')(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.vars.palette.background.default,
}));

export type FooterProps = React.ComponentProps<typeof FooterRoot>;

export function Footer({
  sx,
  layoutQuery = 'md',
  ...other
}: FooterProps & { layoutQuery?: Breakpoint }) {
  return (
    <FooterRoot sx={{ pt: 3, ...sx }} {...other}>
      <Box sx={{ bgcolor: 'primary.main' }}>
        <Container
          sx={(theme: Theme) => ({
            px: { xs: 6, md: 0 },
            pb: 0,
            pt: 3,
            textAlign: 'center',
            [theme.breakpoints.up(layoutQuery)]: { textAlign: 'unset', pt: 10, pb: 5 },
          })}
        >
          <Grid
            container
            sx={[
              (theme: Theme) => ({
                mt: 0,
                flexWrap: 'wrap',
                gap: 1,
                [theme.breakpoints.up(layoutQuery)]: {
                  gap: 0,
                  justifyContent: 'space-between',
                  mt: 3,
                },
              }),
            ]}
          >
            <Grid
              size={{ xs: 12, [layoutQuery]: 3 }}
              sx={{ order: { xs: 1, [layoutQuery]: 1 }, mb: { xs: 3, [layoutQuery]: 0 } }}
            >
              <Logo sx={{ filter: 'brightness(0) invert(1)' }} />

              <Typography
                variant="body2"
                color="common.white"
                sx={(theme: Theme) => ({
                  mx: 'auto',
                  mt: 2,
                  maxWidth: 280,
                  textAlign: { xs: 'center', [layoutQuery]: 'left' },
                  [theme.breakpoints.up(layoutQuery)]: { mx: 'unset' },
                })}
              >
                Hophop aile şehir rehberi ve etkinlik bulma uygulamasıdır. Çocuklar için şehirdeki
                her şey, bu uygulamada.
              </Typography>
            </Grid>

            <Grid
              size={{ xs: 12, [layoutQuery]: 3 }}
              sx={{
                pl: { xs: 0, [layoutQuery]: 2 },
                order: { xs: 2, [layoutQuery]: 2 },
                mb: { xs: 3, [layoutQuery]: 0 },
              }}
            >
              <Box
                sx={(theme: Theme) => ({
                  gap: 1,
                  width: 1,
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  textAlign: 'center',
                  [theme.breakpoints.up(layoutQuery)]: {
                    gap: 2,
                    alignItems: 'flex-start',
                    textAlign: 'left',
                  },
                })}
              >
                <Typography
                  component="div"
                  variant="overline"
                  color="common.white"
                  sx={{
                    textDecoration: 'underline',
                    fontWeight: 'bold',
                    mb: { xs: 1, [layoutQuery]: 1 },
                    fontSize: '1rem',
                  }}
                >
                  Hızlı Linkler
                </Typography>

                {LINKS[0].children.map((link: { name: string; href: string }) => (
                  <Link
                    key={link.name}
                    component={RouterLink}
                    href={link.href}
                    color="common.white"
                    variant="body2"
                    sx={{ mb: 0.5 }}
                  >
                    {link.name}
                  </Link>
                ))}
              </Box>
            </Grid>

            <Grid
              size={{ xs: 12, [layoutQuery]: 3 }}
              sx={{ order: { xs: 3, [layoutQuery]: 3 }, mb: { xs: 3, [layoutQuery]: 0 } }}
            >
              <Box
                sx={(theme: Theme) => ({
                  gap: 1,
                  width: 1,
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  textAlign: 'center',
                  [theme.breakpoints.up(layoutQuery)]: {
                    gap: 2,
                    alignItems: 'flex-start',
                    textAlign: 'left',
                  },
                })}
              >
                <Typography
                  component="div"
                  variant="overline"
                  color="common.white"
                  sx={{
                    textDecoration: 'underline',
                    fontWeight: 'bold',
                    mb: { xs: 1, [layoutQuery]: 1 },
                    fontSize: '1rem',
                  }}
                >
                  Keşfet
                </Typography>

                {KESFET.map((link: { name: string; href: string }) => (
                  <Link
                    key={link.name}
                    component={RouterLink}
                    href={link.href}
                    color="common.white"
                    variant="body2"
                    sx={{ mb: 0.5 }}
                  >
                    {link.name}
                  </Link>
                ))}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, [layoutQuery]: 3 }} sx={{ order: { xs: 4, [layoutQuery]: 4 } }}>
              <Box
                sx={(theme: Theme) => ({
                  gap: 1,
                  width: 1,
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  textAlign: 'center',
                  [theme.breakpoints.up(layoutQuery)]: {
                    gap: 2,
                    alignItems: 'flex-start',
                    textAlign: 'left',
                  },
                })}
              >
                <Typography
                  component="div"
                  variant="overline"
                  color="common.white"
                  sx={{
                    textDecoration: 'underline',
                    fontWeight: 'bold',
                    mb: { xs: 1, [layoutQuery]: 1 },
                    fontSize: '1rem',
                  }}
                >
                  Bize Ulaşın
                </Typography>

                <Link href="tel:+905316464896" sx={{ textDecoration: 'none', mb: 0.5 }}>
                  <Typography variant="body2" color="common.white">
                    0531 6464896
                  </Typography>
                </Link>

                <Typography variant="body2" color="common.white">
                  info@hophop.com.tr
                </Typography>
              </Box>

              <Box
                sx={(theme: Theme) => ({
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 2,
                  mt: 3,
                  [theme.breakpoints.up(layoutQuery)]: {
                    justifyContent: 'flex-start',
                  },
                })}
              >
                {_socials.map((social) => (
                  <IconButton key={social.label} sx={{ p: 0.5, width: 32, height: 32 }}>
                    {social.value === 'twitter' && (
                      <Iconify icon="line-md:twitter-x" width={22} sx={{ color: 'common.white' }} />
                    )}
                    {social.value === 'facebook' && (
                      <Iconify
                        icon="ic:baseline-facebook"
                        width={22}
                        sx={{ color: 'common.white' }}
                      />
                    )}
                    {social.value === 'instagram' && (
                      <Iconify icon="mdi:instagram" width={22} sx={{ color: 'common.white' }} />
                    )}
                    {social.value === 'linkedin' && (
                      <Iconify icon="mdi:linkedin" width={22} sx={{ color: 'common.white' }} />
                    )}
                  </IconButton>
                ))}
              </Box>

              <Box
                sx={(theme: Theme) => ({
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 2,
                  mt: 3,
                  [theme.breakpoints.up(layoutQuery)]: {
                    justifyContent: 'flex-start',
                  },
                })}
              >
                <Link href="#" sx={{ textDecoration: 'none' }}>
                  <Box
                    component="img"
                    src={AppStore}
                    alt="App Store"
                    sx={{ height: 40, cursor: 'pointer' }}
                  />
                </Link>
                <Link href="#" sx={{ textDecoration: 'none' }}>
                  <Box
                    component="img"
                    src={GooglePlay}
                    alt="Google Play"
                    sx={{ height: 40, cursor: 'pointer' }}
                  />
                </Link>
              </Box>
            </Grid>
          </Grid>
        </Container>

        <Divider sx={{ borderColor: '#FFFFFF40', my: 3 }} />

        <Container>
          <Typography
            variant="body2"
            fontWeight={500}
            color="common.white"
            sx={{ textAlign: 'center', pb: 3 }}
          >
            © {new Date().getFullYear()} Tüm hakları saklıdır.
          </Typography>
        </Container>
      </Box>
    </FooterRoot>
  );
}

// ----------------------------------------------------------------------

export function HomeFooter({ sx, ...other }: FooterProps) {
  return (
    <FooterRoot
      sx={[
        {
          py: 5,
          textAlign: 'center',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Container>
        <Logo />
        <Box sx={{ mt: 1, typography: 'caption' }}>
          © All rights reserved.
          <br /> made by
          <Link href="https://minimals.cc/"> minimals.cc </Link>
        </Box>
      </Container>
    </FooterRoot>
  );
}
